import { forwardRef } from 'react';

import { TextField } from '@material-ui/core';
import { TextFieldProps } from '@material-ui/core/TextField';
import cx from 'classnames';

import { useAIContext } from 'lib/common/contexts/AIContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import IQTextSelectionPopper from 'lib/common/hooks/useIQTextSelection/components/IQTextSelectionPopper';
import useAskIQ from 'lib/common/hooks/useIQTextSelection/useIQTextSelection';

import useStyles from './styles';

import './input.scss';

export type TInput = TextFieldProps & {
  small?: boolean;
  noBorder?: boolean;
  noPadding?: boolean;
  ignoreRows?: boolean;
  ariaRequired?: boolean;
  disableIQ?: boolean;
};

function Input(allProps: TInput, ref) {
  const { small, noBorder, noPadding, ignoreRows, ariaRequired, disableIQ, ...props } = allProps;
  const { isSoftphone } = useLayout();
  const {
    state: { IQEnabled, isIQModalOpen },
  } = useAIContext();

  const IQPopperEnabled = !disableIQ && IQEnabled && isSoftphone && Boolean(props.multiline);

  const { popperPosition, selectedText, handleTextSelectionEnd, handleTextSelectionStart, hideAskIQPopper } = useAskIQ({
    IQPopperEnabled,
  });

  const replaceTextCallback = (text: string) => {
    //Allegedly this is deprecated and the only way to have undo redo working
    document.execCommand('insertText', false, text);
    hideAskIQPopper();
  };

  const classes = useStyles({
    isSoftphone: small || isSoftphone,
    multiline: props.multiline,
    noBorder,
    noPadding,
    ignoreRows,
    disabled: props.disabled,
  });

  return (
    <>
      {IQPopperEnabled && (
        <IQTextSelectionPopper
          popperPosition={isIQModalOpen ? null : popperPosition}
          selectedText={selectedText}
          replaceTextCallback={replaceTextCallback}
          onClosePopper={hideAskIQPopper}
        />
      )}

      <TextField
        variant="outlined"
        {...props}
        ref={ref}
        className={cx(classes.root, props.className)}
        inputProps={{
          ...(props.inputProps || {}),
          className: cx(classes.input, props.inputProps?.className),
          'aria-label': props['aria-label'] || props.label?.toString() || props.placeholder,
          'aria-disabled': props.disabled,
          ...(ariaRequired || props.required ? { 'aria-required': true } : {}),
          ...(IQPopperEnabled
            ? {
                onPointerDown: handleTextSelectionStart,
                onPointerUp: handleTextSelectionEnd,
                onKeyDown: hideAskIQPopper,
              }
            : {}),
        }}
        InputProps={{
          ...(props.InputProps || {}),
          classes: {
            ...(props.InputProps?.classes || {}),
            root: classes.inputRoot,
          },
          ...(ariaRequired || props.required ? { required: true } : {}),
        }}
        InputLabelProps={{
          ...(props.InputLabelProps || {}),
          classes: {
            ...(props.InputLabelProps?.classes || {}),
            root: classes.inputLabelRoot,
          },
        }}
      />
    </>
  );
}

export default forwardRef(Input);
